import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUsers, FiCheck, FiAlertCircle, FiCheckSquare, FiSquare, FiSearch } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--border-color);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s;
  
  &:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
`;

const StudentList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-secondary);
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: var(--bg-hover);
  }
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const Checkbox = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0;
  transition: all 0.2s;
  
  &:hover {
    color: var(--primary-color);
  }
  
  &.selected {
    color: var(--primary-color);
  }
`;

const StudentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StudentName = styled.span`
  font-weight: 500;
  font-size: 0.875rem;
`;

const StudentEmail = styled.span`
  font-size: 0.75rem;
  opacity: 0.7;
`;

const SelectionSummary = styled.div`
  background: var(--bg-secondary);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SummaryText = styled.span`
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const SelectAllButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s;
  
  &:hover {
    background: var(--bg-hover);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background-color: var(--background-secondary);
      border-color: var(--border-dark);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  color: var(--error-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(104, 211, 145, 0.1);
  border: 1px solid var(--success-color);
  color: var(--success-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--bg-secondary);
  border-radius: 0;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--primary-color);
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

function BulkAssignModal({ isOpen, onClose, course, onStudentsAssigned }) {
  const [students, setStudents] = useState([]);
  const [, setEnrolledStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(0);

  const fetchStudents = useCallback(async () => {
    if (!course || !course.id) {
      setError('No course selected');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Fetch all users
      const usersResponse = await apiService.getUsers();
      if (!usersResponse.success) {
        throw new Error('Failed to fetch users');
      }
      
      // Get course details to fetch enrolled students
      const courseResponse = await apiService.getCourse(course.id);
      if (!courseResponse.success) {
        throw new Error('Failed to fetch course details');
      }
      
      // Filter only students who are not already enrolled in this course
      const allStudents = usersResponse.users.filter(user => user.role === 'student');
      const enrolledStudentIds = courseResponse.course.students?.map(student => student.id) || [];
      const availableStudents = allStudents.filter(student => !enrolledStudentIds.includes(student.id));
      
      setStudents(availableStudents);
      setEnrolledStudents(enrolledStudentIds);
      setSelectedStudents([]);
      setSearchTerm('');
      
    } catch (error) {
      logger.error('Error fetching students', { error, courseId: course?.id });
      setError('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  }, [course]);

  useEffect(() => {
    if (isOpen) {
      fetchStudents();
    }
  }, [isOpen, fetchStudents]);

  const toggleStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    const filteredStudents = getFilteredStudents();
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(student => student.id));
    }
  };

  const getFilteredStudents = () => {
    if (!searchTerm.trim()) {
      return students;
    }
    
    const term = searchTerm.toLowerCase();
    return students.filter(student => 
      student.first_name.toLowerCase().includes(term) ||
      student.last_name.toLowerCase().includes(term) ||
      student.email.toLowerCase().includes(term)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!course || !course.id) {
      setError('No course selected');
      return;
    }
    
    if (selectedStudents.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      setAssigning(true);
      setError('');
      setSuccess('');
      setProgress(0);

      const totalStudents = selectedStudents.length;
      let successCount = 0;
      let failedStudents = [];

      for (let i = 0; i < selectedStudents.length; i++) {
        const studentId = selectedStudents[i];
        try {
          const response = await apiService.assignStudentToCourse(course.id, studentId);
          if (response.success) {
            successCount++;
          } else {
            failedStudents.push(studentId);
          }
        } catch (error) {
          failedStudents.push(studentId);
        }
        
        // Update progress
        setProgress(((i + 1) / totalStudents) * 100);
      }

      if (successCount > 0) {
        setSuccess(`Successfully assigned ${successCount} student${successCount > 1 ? 's' : ''} to course`);
        onStudentsAssigned(selectedStudents.filter(id => !failedStudents.includes(id)));
        
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      }

      if (failedStudents.length > 0) {
        setError(`${failedStudents.length} student${failedStudents.length > 1 ? 's' : ''} could not be assigned`);
      }
    } catch (error) {
      logger.error('Error assigning students', { error, studentIds: selectedStudents, courseId: course?.id });
      setError('Failed to assign students to course');
    } finally {
      setAssigning(false);
      setProgress(0);
    }
  };

  const handleClose = () => {
    setSelectedStudents([]);
    setSearchTerm('');
    setError('');
    setSuccess('');
    setProgress(0);
    onClose();
  };

  // const isAllSelected = students.length > 0 && selectedStudents.length === students.length;
  const isSomeSelected = selectedStudents.length > 0;

  // Don't render if no course is provided
  if (!course) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiUsers size={20} />
                Bulk Assign Students
              </ModalTitle>
              <CloseButton onClick={handleClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Course</Label>
                <div style={{ 
                  padding: '0.75rem', 
                  background: 'var(--bg-secondary)', 
                  borderRadius: 0,
                  border: '1px solid var(--border-color)'
                }}>
                  <strong>{course.code}</strong> - {course.name}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Select Students</Label>
                <SearchContainer>
                  <SearchIcon>
                    <FiSearch size={16} />
                  </SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Search students by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchContainer>
                <SelectionSummary>
                  <SummaryText>
                    {selectedStudents.length} of {getFilteredStudents().length} students selected
                  </SummaryText>
                  <SelectAllButton onClick={selectAllStudents}>
                    {selectedStudents.length === getFilteredStudents().length && getFilteredStudents().length > 0 ? 'Deselect All' : 'Select All'}
                  </SelectAllButton>
                </SelectionSummary>
                
                {loading ? (
                  <div style={{ 
                    padding: '1rem', 
                    textAlign: 'center',
                    color: 'var(--text-secondary)'
                  }}>
                    <LoadingSpinner />
                    Loading students...
                  </div>
                ) : (
                  <StudentList>
                    {students.length === 0 ? (
                      <div style={{ 
                        padding: '1rem', 
                        textAlign: 'center',
                        color: 'var(--text-secondary)'
                      }}>
                        No students available
                      </div>
                    ) : (
                      getFilteredStudents().length === 0 ? (
                        <div style={{ 
                          padding: '1rem', 
                          textAlign: 'center',
                          color: 'var(--text-secondary)'
                        }}>
                          {searchTerm ? 'No students found matching your search' : 'No students available'}
                        </div>
                      ) : (
                        getFilteredStudents().map(student => {
                          const isSelected = selectedStudents.includes(student.id);
                          return (
                            <StudentItem
                              key={student.id}
                              onClick={() => toggleStudent(student.id)}
                            >
                              <StudentInfo>
                                <Checkbox 
                                  className={isSelected ? 'selected' : ''}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {isSelected ? <FiCheckSquare size={16} /> : <FiSquare size={16} />}
                                </Checkbox>
                                <StudentDetails>
                                  <StudentName>
                                    {student.first_name} {student.last_name}
                                  </StudentName>
                                  <StudentEmail>{student.email}</StudentEmail>
                                </StudentDetails>
                              </StudentInfo>
                            </StudentItem>
                          );
                        })
                      )
                    )}
                  </StudentList>
                )}
              </FormGroup>

              {assigning && (
                <FormGroup>
                  <Label>Assigning students... {Math.round(progress)}%</Label>
                  <ProgressBar>
                    <ProgressFill progress={progress} />
                  </ProgressBar>
                </FormGroup>
              )}

              {error && (
                <ErrorMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiAlertCircle size={16} />
                  {error}
                </ErrorMessage>
              )}

              {success && (
                <SuccessMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiCheck size={16} />
                  {success}
                </SuccessMessage>
              )}

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="primary" 
                  disabled={loading || assigning || !isSomeSelected}
                >
                  {assigning ? (
                    <>
                      <LoadingSpinner />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      Assign {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default BulkAssignModal;
